import React, { useState } from 'react';
import { api } from '../lib/api';
import { daysBetween, fmtDate, money, today } from '../lib/format';
import type { Lease, SkipReason } from '../lib/types';
import { Drawer, Failed, Loading, Stat, type Ctx } from '../main';
import { useResource } from '../lib/hooks';

/** The engine's own words for why a lease is skipped, in ours. */
const SKIP: Record<SkipReason, string> = {
  lease_ended: 'Lease has ended',
  moving_out: 'Moving out',
  rent_paused: 'Rent paused — emergency',
  lease_expires_first: 'Lease expires before the due date',
  already_scheduled: 'Already scheduled',
  not_started: 'Not started — the move-in bills the first month',
};

export function Leases({ ctx }: { ctx: Ctx }): React.JSX.Element {
  const leases = useResource(() => api.leases(), []);
  const cycle = useResource(() => api.rentCycle().catch(() => null), []);
  const [open, setOpen] = useState<Lease | null>(null);

  if (leases.loading) return <Loading />;
  if (leases.error) return <Failed error={leases.error} onRetry={leases.reload} />;
  const list = leases.data?.leases ?? [];
  const rc = cycle.data;
  const skipped = rc?.plan.filter((p) => p.skip) ?? [];

  return (
    <>
      {rc && (
        <>
          <div className="grid g3" style={{ marginBottom: 16 }}>
            <Stat label="Next rent run" value={rc.willCreate}
              foot={rc.windowOpen ? 'Window is open' : `Opens ${fmtDate(rc.windowOpensOn)}`} />
            <Stat label="Value" value={money(rc.totalValue)} foot="Tasks the run will create" />
            <Stat label="Skipped" value={skipped.length} foot="With a reason, below" />
          </div>
          <div className="note" style={{ marginBottom: 16 }}>
            Each lease gets its rent task in the last five days of the month.
          </div>
          {skipped.length > 0 && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="hd"><h3>Skipped next run</h3></div>
              <table className="responsive">
                <thead><tr><th>Lease</th><th>Amount</th><th>Reason</th></tr></thead>
                <tbody>{skipped.map((p) => (
                  <tr key={p.leaseId}>
                    <td data-label="Lease">{list.find((l) => l.id === p.leaseId)?.tenantName ?? <span className="tid">{p.leaseId.slice(0, 8)}</span>}</td>
                    <td data-label="Amount" className="mono">{money(p.amount)}</td>
                    <td data-label="Reason">
                      <span className="tag t-mute">{p.skip ? SKIP[p.skip] : ''}</span>
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </>
      )}

      <div className="card"><table className="responsive">
        <thead><tr><th>Tenant</th><th>Where</th><th>Term</th><th>Rent</th><th>Deposit</th><th>Status</th><th>Ends in</th></tr></thead>
        <tbody>{list.map((l) => {
          const left = daysBetween(today(), l.endDate);
          return (
            <tr key={l.id} className="clk" onClick={() => setOpen(l)}>
              <td data-label="Tenant"><b>{l.tenantName ?? '—'}</b></td>
              <td data-label="Where">{ctx.propertyName(l.propertyId)}{l.roomNumber ? ` · Room ${l.roomNumber}` : ''}</td>
              <td data-label="Term" className="mono">{fmtDate(l.startDate)} → {fmtDate(l.endDate)}</td>
              <td data-label="Rent" className="mono">{money(l.monthlyRent)}</td>
              <td data-label="Deposit">
                <span className={`tag t-${l.depositStatus === 'Paid' ? 'ok' : 'warn'}`}>{l.depositStatus}</span>
              </td>
              <td data-label="Status">
                <span className={`tag t-${l.status}`}>{l.status}</span>
                {l.rentPausedOn && <span className="tag t-bad" style={{ marginLeft: 5 }}>rent paused</span>}
              </td>
              <td data-label="Ends in" className={left < 16 ? 'late' : 'mono'}>{left}d</td>
            </tr>
          );
        })}</tbody>
      </table>
      {list.length === 0 && <div className="empty"><b>No leases</b>A lease is created when a tenant deal completes.</div>}
      </div>

      {open && (
        <LeaseDrawer ctx={ctx} lease={open} onClose={() => setOpen(null)}
          onChanged={async () => { await leases.reload(); await cycle.reload(); await ctx.refresh(); setOpen(null); }} />
      )}
    </>
  );
}

function LeaseDrawer({ ctx, lease, onClose, onChanged }: {
  ctx: Ctx; lease: Lease; onClose: () => void; onChanged: () => Promise<void>;
}): React.JSX.Element {
  const [busy, setBusy] = useState(false);
  const left = daysBetween(today(), lease.endDate);

  const startMoveOut = async (): Promise<void> => {
    setBusy(true);
    try {
      await api.moveOut(lease.id);
      ctx.toast('Moving out started', 'Move out, deposit inspection and check out follow once the notice is done.');
      await onChanged();
    } catch (e) {
      ctx.toast('Could not start', (e as Error).message);
      setBusy(false);
    }
  };

  return (
    <Drawer title={lease.tenantName ?? ctx.propertyName(lease.propertyId)}
      subtitle={`${ctx.propertyName(lease.propertyId)}${lease.roomNumber ? ` · Room ${lease.roomNumber}` : ''} · ${fmtDate(lease.startDate)} → ${fmtDate(lease.endDate)}`}
      onClose={onClose}
      footer={<>
        <button className="btn" onClick={onClose}>Close</button>
        {lease.status === 'Active' && (
          <button className="btn danger w" disabled={busy} onClick={startMoveOut}>
            {busy ? 'Starting…' : 'Start move-out'}
          </button>
        )}
      </>}>
      {lease.rentPausedOn && (
        <div className="note bad" style={{ marginBottom: 14 }}>
          <b>Rent is paused</b> since {fmtDate(lease.rentPausedOn)}. The rent run skips this lease
          until an emergency resolves and the engine resumes it.
        </div>
      )}
      {left < 16 && lease.status === 'Active' && (
        <div className="note warn" style={{ marginBottom: 14 }}>
          Inside the last 15 days. The Moving Out workflow starts on its own tonight if nobody
          starts it first.
        </div>
      )}
      <dl className="kv">
        <dt>Status</dt><dd><span className={`tag t-${lease.status}`}>{lease.status}</span></dd>
        <dt>Monthly rent</dt><dd className="mono">{money(lease.monthlyRent)}</dd>
        <dt>Deposit</dt><dd className="mono">{money(lease.depositAmount)} · {lease.depositStatus}</dd>
        <dt>Ends</dt><dd className="mono">{fmtDate(lease.endDate)} · {left} days</dd>
      </dl>
      <div className="note" style={{ marginTop: 14 }}>
        Starting a move-out creates the notice task. Everything after it — the inspection, the
        deposit refund, the check-out, the turnover clean and the relist — is created by the engine
        as each one completes.
      </div>
    </Drawer>
  );
}
